import { useState, useCallback } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { AppLayout } from '@/components/layout/AppLayout';
import { AdminGuard } from '@/components/guards/AdminGuard';
import { PageHeader } from '@/components/layout/PageHeader';
import { ModuleConfigList } from '@/components/admin/ModuleConfigList';
import { ModuleConfigEditor } from '@/components/admin/ModuleConfigEditor';
import { AddModuleDialog } from '@/components/admin/AddModuleDialog';
import { DeleteModuleDialog } from '@/components/admin/DeleteModuleDialog';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { useToast } from '@/components/ui/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { moduleConfigApi, moduleConfigKeys } from '@/lib/api';
import type {
  ModuleConfigurationAdmin,
  CreateModuleConfigRequest,
  UpdateModuleConfigRequest,
  ApiError,
} from '@/types';

export const Route = createFileRoute('/admin/modules')({
  component: AdminModulesPage,
});

function AdminModulesPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [selectedModule, setSelectedModule] = useState<ModuleConfigurationAdmin | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState<ModuleConfigurationAdmin | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const {
    data: modules,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: moduleConfigKeys.list(),
    queryFn: () => moduleConfigApi.list(),
  });

  const { mutate: updateModule, isPending: isUpdating } = useMutation({
    mutationFn: ({ key, data }: { key: string; data: UpdateModuleConfigRequest }) =>
      moduleConfigApi.update(key, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: moduleConfigKeys.all });
      setIsEditorOpen(false);
      toast({
        title: 'Module Updated',
        description: 'Module configuration has been saved.',
      });
    },
    // The API client rejects with an ApiError (ProblemDetails), not an Error — read detail
    // so conflicts like "product type 18 is already mapped by 'e360'" reach the admin.
    onError: (error: ApiError) => {
      toast({
        title: 'Update Failed',
        description: error.detail ?? 'Failed to update module configuration.',
        variant: 'destructive',
      });
    },
  });

  const { mutate: createModule, isPending: isCreating } = useMutation({
    mutationFn: (data: CreateModuleConfigRequest) => moduleConfigApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: moduleConfigKeys.all });
      setIsAddDialogOpen(false);
      toast({
        title: 'Module Created',
        description: 'New module configuration has been created.',
      });
    },
    onError: (error: ApiError) => {
      toast({
        title: 'Creation Failed',
        description: error.detail ?? 'Failed to create module configuration.',
        variant: 'destructive',
      });
    },
  });

  const { mutate: deleteModule, isPending: isDeleting } = useMutation({
    mutationFn: (key: string) => moduleConfigApi.delete(key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: moduleConfigKeys.all });
      setIsDeleteDialogOpen(false);
      setModuleToDelete(null);
      toast({ title: 'Module deleted', variant: 'success' });
    },
    onError: (error: ApiError) => {
      toast({
        title: 'Delete failed',
        description: error.detail ?? error.title,
        variant: 'destructive',
      });
    },
  });

  const handleCreate = (data: CreateModuleConfigRequest) => {
    createModule(data);
  };

  const handleEdit = (module: ModuleConfigurationAdmin) => {
    setSelectedModule(module);
    setIsEditorOpen(true);
  };

  const handleToggleEnabled = (key: string, enabled: boolean) => {
    updateModule({ key, data: { isEnabled: enabled } });
  };

  const handleSave = (key: string, data: UpdateModuleConfigRequest) => {
    updateModule({ key, data });
  };

  const handleDelete = (moduleKey: string) => {
    const module = modules?.find((m) => m.moduleKey === moduleKey) ?? null;
    setModuleToDelete(module);
    setIsDeleteDialogOpen(true);
  };

  const handleReorder = useCallback(async (orderedKeys: string[]) => {
    // Update each module's displayOrder based on new position
    const updates = orderedKeys.map((key, index) =>
      moduleConfigApi.update(key, { displayOrder: index + 1 }),
    );
    try {
      await Promise.all(updates);
      queryClient.invalidateQueries({ queryKey: moduleConfigKeys.all });
    } catch {
      toast({
        title: 'Reorder Failed',
        description: 'Failed to save the new module order.',
        variant: 'destructive',
      });
    }
  }, [queryClient, toast]);

  return (
    <AdminGuard>
      <AppLayout>
        <div className="container max-w-5xl py-8">
          <PageHeader
            title="Module Configuration"
            description="Configure migration module settings and endpoints"
            actions={
              <Button onClick={() => setIsAddDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Module
              </Button>
            }
          />

          {isLoading ? (
            <div className="flex items-center justify-center min-h-[400px]">
              <LoadingSpinner size="lg" label="Loading modules..." />
            </div>
          ) : isError ? (
            <ErrorMessage
              title="Failed to Load Modules"
              error={error}
              onRetry={() => refetch()}
            />
          ) : (
            <ModuleConfigList
              modules={modules ?? []}
              onEdit={handleEdit}
              onToggleEnabled={handleToggleEnabled}
              onDelete={handleDelete}
              onReorder={handleReorder}
              isUpdating={isUpdating}
            />
          )}

          <ModuleConfigEditor
            open={isEditorOpen}
            onOpenChange={setIsEditorOpen}
            module={selectedModule}
            onSubmit={handleSave}
            isSubmitting={isUpdating}
          />

          <AddModuleDialog
            open={isAddDialogOpen}
            onOpenChange={setIsAddDialogOpen}
            onSubmit={handleCreate}
            isSubmitting={isCreating}
          />

          <DeleteModuleDialog
            open={isDeleteDialogOpen}
            onOpenChange={setIsDeleteDialogOpen}
            module={moduleToDelete}
            onConfirm={(key) => deleteModule(key)}
            isDeleting={isDeleting}
          />

          <Toaster />
        </div>
      </AppLayout>
    </AdminGuard>
  );
}
